/**
 * The page for team management.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2026
 */
import { fetchUser, fetchUserComplete } from '@/api'
import IconButton from '@/components/Button/IconButton'
import { PageNote } from '@/components/PageNote'
import { getUserState, useSelector } from '@/store'
import { AccountUtil } from '@amplify3/duplo-back-end'
import { ReactNode, useEffect, useState } from 'react'
import InviteMemberDialog from './InviteMemberDialog'
import PendingInvitationList from './PendingInvitationList'
import TeamMemberList from './TeamMemberList'
import './TeamPage.css'

interface TeamPageProps {
  id?: string
}

export const TeamPage = (props: TeamPageProps) => {
  const { userInfo, userInfoComplete } = useSelector(getUserState)
  const [isInviteDialogVisible, setInviteDialogVisible] = useState(false)
  const isAdminUser = AccountUtil.isAdminUser(userInfo)

  useEffect(() => {
    fetchUser()
    fetchUserComplete()
  }, [])

  const InviteButton = () => (
    <IconButton
      label="+ Invite member"
      id="invite-button"
      onClick={() => setInviteDialogVisible(true)}
    />
  )

  return (
    <div className={props.id ? `page team-page-${props.id}` : 'page team-page'}>
      {isAdminUser && (
        <PageNote
          message={
            'Only admin accounts can invite members and ' +
            'access API credentials and ' +
            'team management sections.'
          }
        />
      )}

      <TeamContainer
        title="Members"
        count={userInfoComplete?.users.length ?? 0}
        titleEndComponent={isAdminUser && <InviteButton />}
      >
        <TeamMemberList
          currentUser={userInfo}
          list={userInfoComplete?.users ?? []}
        />
      </TeamContainer>

      {isAdminUser && (
        <TeamContainer
          title="Pending"
          count={userInfoComplete?.pending_invitations?.length ?? 0}
        >
          <PendingInvitationList
            list={userInfoComplete?.pending_invitations ?? []}
          />
        </TeamContainer>
      )}

      {userInfo?.company_id && (
        <InviteMemberDialog
          company_id={userInfo?.company_id}
          isShowing={isInviteDialogVisible}
          show={setInviteDialogVisible}
        ></InviteMemberDialog>
      )}
    </div>
  )
}

interface TeamContainerProps {
  children?: ReactNode
  title: string
  titleEndComponent?: ReactNode
  count: number
}

const TeamContainer = (props: TeamContainerProps) => (
  <div className="team-container">
    <div className="title-container">
      <div className="title">{props.title}</div>
      <div className="counter">({props.count})</div>
      {props.titleEndComponent && (
        <div className="title-end-component">{props.titleEndComponent}</div>
      )}
    </div>
    {props.children}
  </div>
)
